import { gql } from "@apollo/client";
import { useMemo } from "react";
import useSWR from "swr";

import { getServerUrl } from "config/backend";
import { USD_DECIMALS } from "config/factors";
import { CHART_PERIODS } from "lib/legacy";
import { formatAmount } from "lib/numbers";
import { appendOracleNetworkParam } from "lib/oracleNetwork";
import { sleep } from "lib/sleep";
import { getNativeToken, getNormalizedTokenSymbol, isChartAvailableForToken } from "sdk/configs/tokens";

import { FEED_ID_MAP } from "./constants";
import type { Bar, FromOldToNewArray } from "../tradingview/types";

type CandleTuple = [number, number, number, number, number];

type CandlesResponse = {
  candles: CandleTuple[];
};

function formatCandleTupleToBar(tuple: CandleTuple): Bar {
  const [t, o, h, l, c] = tuple;
  return {
    time: t,
    open: o,
    close: c,
    high: h,
    low: l,
  };
}

export function fillGaps(prices: Bar[], periodSeconds: number) {
  if (prices.length < 2) {
    return prices;
  }

  const newPrices = [prices[0]];
  let prevTime = prices[0].time;
  for (let i = 1; i < prices.length; i++) {
    const { time, open } = prices[i];
    if (prevTime) {
      let j = (time - prevTime) / periodSeconds - 1;
      while (j > 0) {
        newPrices.push({
          time: time - j * periodSeconds,
          open,
          close: open,
          high: open * 1.0003,
          low: open * 0.9996,
        });
        j--;
      }
    }

    prevTime = time;
    newPrices.push(prices[i]);
  }

  return newPrices;
}

export async function getLimitChartPricesFromStats(
  chainId: number,
  symbol: string,
  period: string,
  limit = 1
): Promise<FromOldToNewArray<Bar>> {
  symbol = getNormalizedTokenSymbol(symbol);

  if (!isChartAvailableForToken(chainId, symbol)) {
    symbol = getNativeToken(chainId).symbol;
  }

  const baseUrl = getServerUrl(chainId, "/oracle-keeper/prices/candles");
  const params = appendOracleNetworkParam(
    new URLSearchParams({
      tokenSymbol: symbol,
      period,
      limit: String(limit),
    }),
    chainId
  );
  const url = `${baseUrl}?${params.toString()}`;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const { candles }: CandlesResponse = await response.json();
    return candles.map(formatCandleTupleToBar);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Error fetching data: ${error}`);
    return [];
  }
}

export async function getChartPricesFromStats(
  chainId: number,
  symbol: string,
  period: string
): Promise<FromOldToNewArray<Bar>> {
  symbol = getNormalizedTokenSymbol(symbol);

  const timeDiff = CHART_PERIODS[period] * 3000;
  // const from = Math.floor(Date.now() / 1000 - timeDiff); // Not strictly required by API 1.1 but kept if needed for logic

  // API Specification only mentions limit, but typically history needs a limit anyway.
  // We'll requests a large limit to cover sufficient history for the chart.
  const LIMIT = 50000;

  const baseUrl = getServerUrl(chainId, "/oracle-keeper/prices/candles");
  const params = appendOracleNetworkParam(
    new URLSearchParams({
      tokenSymbol: symbol,
      period,
      limit: String(LIMIT),
    }),
    chainId
  );
  const url = `${baseUrl}?${params.toString()}`;

  const TIMEOUT = 10000;
  const res: Response = await new Promise(async (resolve, reject) => {
    let done = false;
    setTimeout(() => {
      done = true;
      reject(new Error(`request timeout ${url}`));
    }, TIMEOUT);

    let lastEx;
    for (let i = 0; i < 3; i++) {
      if (done) return;
      try {
        const res = await fetch(url);
        resolve(res);
        return;
      } catch (ex) {
        await sleep(300);
        lastEx = ex;
      }
    }
    reject(lastEx);
  });
  if (!res.ok) {
    throw new Error(`request failed ${res.status} ${res.statusText}`);
  }
  const json: CandlesResponse = await res.json();
  const candles = json?.candles;

  if (!candles || candles.length < 1) {
    throw new Error(`not enough prices data: ${candles?.length}`);
  }

  // Check generic obsolescence if needed, assuming the last candle is recent
  const lastCandle = candles[0]; // Assuming latest first based on spec? "latest first"
  // Spec says: "latest first".
  // However, TradingView chart usually expects ascending order.
  // We should check the order. If [0] > [1], it's descending.

  // Standard TV is ascending.
  // If API returns latest first (descending), we should reverse it.

  // Let's inspect the sample response:
  // [1704230000, ...]
  // One sample. Hard to tell order.
  // Usually API "limit" with "latest first" implies descending.
  // We'll reverse if necessary.

  const prices = candles.map(formatCandleTupleToBar);

  // Ensure ascending order for TradingView
  if (prices.length > 1 && prices[0].time > prices[1].time) {
    prices.reverse();
  }

  return prices;
}

function getCandlesFromPrices(prices: any[], period: string) {
  // Utility preserved if needed, though fillGaps covers most
  const periodTime = CHART_PERIODS[period];

  if (prices.length < 2) {
    return [];
  }

  const candles: any[] = [];
  const first = prices[0];
  let prevTsGroup = Math.floor(first[0] / periodTime) * periodTime;
  let prevPrice = first[1];
  let o = prevPrice;
  let h = prevPrice;
  let l = prevPrice;
  let c = prevPrice;
  for (let i = 1; i < prices.length; i++) {
    const [ts, price] = prices[i];
    const tsGroup = Math.floor(ts / periodTime) * periodTime;
    if (prevTsGroup !== tsGroup) {
      candles.push({ t: prevTsGroup, o, h, l, c });
      o = c;
      h = Math.max(o, c);
      l = Math.min(o, c);
    }
    c = price;
    h = Math.max(h, price);
    l = Math.min(l, price);
    prevTsGroup = tsGroup;
  }

  return candles.map(({ t: time, o: open, c: close, h: high, l: low }) => ({
    time,
    open,
    close,
    high,
    low,
  }));
}

export function getChainlinkChartPricesFromGraph(tokenSymbol: string, period: string): Promise<FromOldToNewArray<Bar>> {
  // Chainlink subgraph fallback has been disabled.
  return Promise.resolve([]);
}

export function useChartPrices(
  chainId: number,
  symbol: string,
  isStable: boolean,
  period: string,
  currentAveragePrice: any
) {
  const swrKey = !isStable && symbol ? ["getChartCandles", chainId, symbol, period] : null;
  let { data: prices, mutate: updatePrices } = useSWR(swrKey, {
    fetcher: async () => {
      try {
        return await getChartPricesFromStats(chainId, symbol, period);
      } catch (ex) {
        // eslint-disable-next-line no-console
        console.warn(ex);
        return [];
      }
    },
    dedupingInterval: 60000,
    focusThrottleInterval: 60000 * 10,
  });

  const currentAveragePriceString = currentAveragePrice && currentAveragePrice.toString();
  const retPrices = useMemo(() => {
    if (isStable) {
      return getStablePriceData(period);
    }

    if (!prices) {
      return [];
    }

    let _prices = [...prices];
    if (currentAveragePriceString && prices.length) {
      _prices = appendCurrentAveragePrice(_prices, BigInt(currentAveragePriceString), period);
    }

    return fillGaps(_prices, CHART_PERIODS[period]);
  }, [prices, isStable, currentAveragePriceString, period]);

  return [retPrices, updatePrices];
}

function appendCurrentAveragePrice(prices: Bar[], currentAveragePrice: bigint, period: string) {
  const periodSeconds = CHART_PERIODS[period];
  const currentCandleTime = Math.floor(Date.now() / 1000 / periodSeconds) * periodSeconds;
  const last = prices[prices.length - 1];
  const averagePriceValue = parseFloat(formatAmount(currentAveragePrice, USD_DECIMALS, 2));
  if (currentCandleTime === last.time) {
    last.close = averagePriceValue;
    last.high = Math.max(last.open, last.high, averagePriceValue);
    last.low = Math.min(last.open, last.low, averagePriceValue);
    return prices;
  } else {
    const newCandle = {
      time: currentCandleTime,
      open: last.close,
      close: averagePriceValue,
      high: averagePriceValue,
      low: averagePriceValue,
    };
    return [...prices, newCandle];
  }
}

export function getStablePriceData(period: string, countBack = 100) {
  const periodSeconds = CHART_PERIODS[period];
  const now = Math.floor(Date.now() / 1000 / periodSeconds) * periodSeconds;
  let priceData: any = [];
  for (let i = countBack; i > 0; i--) {
    priceData.push({
      time: now - i * periodSeconds,
      open: 1,
      close: 1,
      high: 1,
      low: 1,
    });
  }
  return priceData;
}
